import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import transferPinAPI from '../services/transferPinAPI';

const TransferPinModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  mode = 'validate', // 'validate', 'set', 'change'
  title = 'Transfer PIN Required',
  description = 'Please enter your 6-digit transfer PIN to continue'
}) => {
  const [pin, setPin] = useState('');
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [pinStatus, setPinStatus] = useState(null);

  useEffect(() => {
    if (isOpen && mode === 'set') {
      checkPinStatus();
    }
  }, [isOpen, mode]);

  const checkPinStatus = async () => {
    try {
      const response = await transferPinAPI.getStatus();
      setPinStatus(response.data);
    } catch (error) {
      console.error('Error checking PIN status:', error);
    }
  };

  const handleValidatePin = async () => {
    if (pin.length !== 6) {
      setError('Please enter a 6-digit PIN');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await transferPinAPI.validatePin(pin);
      if (response.success) {
        onSuccess(pin);
        onClose();
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Invalid PIN. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPin = async () => {
    if (newPin.length !== 6) {
      setError('PIN must be exactly 6 digits');
      return;
    }

    if (newPin !== confirmPin) {
      setError('PINs do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await transferPinAPI.setPin(newPin, confirmPin);
      if (response.success) {
        onSuccess(newPin);
        onClose();
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to set PIN. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePin = async () => {
    if (currentPin.length !== 6 || newPin.length !== 6) {
      setError('All PINs must be exactly 6 digits');
      return;
    }

    if (newPin !== confirmPin) {
      setError('New PINs do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await transferPinAPI.changePin(currentPin, newPin, confirmPin);
      if (response.success) {
        onSuccess(newPin);
        onClose();
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to change PIN. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPin = async () => {
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await transferPinAPI.resetPin(password, true);
      if (response.success) {
        onSuccess(null);
        onClose();
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to reset PIN. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    switch (mode) {
      case 'validate':
        handleValidatePin();
        break;
      case 'set':
        handleSetPin();
        break;
      case 'change':
        handleChangePin();
        break;
      case 'reset':
        handleResetPin();
        break;
      default:
        handleValidatePin();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handlePinInput = (value, setter) => {
    // Only allow digits and limit to 6 characters
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setter(numericValue);
    setError('');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl p-6 w-full max-w-md mx-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">{title}</h2>
            <p className="text-gray-600 text-sm">{description}</p>
          </div>

          {/* PIN Status Info */}
          {pinStatus && (
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">PIN Status:</span>
                <span className={`font-medium ${pinStatus.has_pin ? 'text-green-600' : 'text-red-600'}`}>
                  {pinStatus.has_pin ? 'Set' : 'Not Set'}
                </span>
              </div>
              {pinStatus.is_locked && (
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-gray-600">Locked until:</span>
                  <span className="font-medium text-red-600">
                    {new Date(pinStatus.locked_until).toLocaleTimeString()}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          {/* Form Content */}
          <div className="space-y-4">
            {mode === 'validate' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter your 6-digit transfer PIN
                </label>
                <input
                  type="password"
                  value={pin}
                  onChange={(e) => handlePinInput(e.target.value, setPin)}
                  onKeyPress={handleKeyPress}
                  placeholder="000000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                  maxLength="6"
                  autoFocus
                />
              </div>
            )}

            {mode === 'set' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Create a 6-digit transfer PIN
                  </label>
                  <input
                    type="password"
                    value={newPin}
                    onChange={(e) => handlePinInput(e.target.value, setNewPin)}
                    placeholder="000000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                    maxLength="6"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm your PIN
                  </label>
                  <input
                    type="password"
                    value={confirmPin}
                    onChange={(e) => handlePinInput(e.target.value, setConfirmPin)}
                    placeholder="000000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                    maxLength="6"
                  />
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                  <p className="text-xs text-yellow-800">
                    <strong>Security Tips:</strong> Choose a PIN that's easy for you to remember but hard for others to guess. 
                    Avoid sequential numbers (123456) or repeated digits (111111).
                  </p>
                </div>
              </div>
            )}

            {mode === 'change' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current PIN
                  </label>
                  <input
                    type="password"
                    value={currentPin}
                    onChange={(e) => handlePinInput(e.target.value, setCurrentPin)}
                    placeholder="000000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                    maxLength="6"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New PIN
                  </label>
                  <input
                    type="password"
                    value={newPin}
                    onChange={(e) => handlePinInput(e.target.value, setNewPin)}
                    placeholder="000000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                    maxLength="6"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New PIN
                  </label>
                  <input
                    type="password"
                    value={confirmPin}
                    onChange={(e) => handlePinInput(e.target.value, setConfirmPin)}
                    placeholder="000000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                    maxLength="6"
                  />
                </div>
              </div>
            )}

            {mode === 'reset' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter your password to reset PIN
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Your account password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-2">
                  This will completely remove your transfer PIN. You'll need to set a new one for future transfers.
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 mt-6">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 py-3 px-4 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                mode === 'validate' ? 'Verify PIN' :
                mode === 'set' ? 'Set PIN' :
                mode === 'change' ? 'Change PIN' :
                'Reset PIN'
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TransferPinModal;
